import anthropicLogoUrl from '@/assets/provider-logos/anthropic.svg'
import deepseekLogoUrl from '@/assets/provider-logos/deepseek.svg'
import geminiLogoUrl from '@/assets/provider-logos/gemini.svg'
import glmLogoUrl from '@/assets/provider-logos/glm.svg'
import kimiLogoUrl from '@/assets/provider-logos/kimi.svg'
import minimaxLogoUrl from '@/assets/provider-logos/minimax.svg'
import openaiLogoUrl from '@/assets/provider-logos/openai.svg'
import qwenLogoUrl from '@/assets/provider-logos/qwen.svg'
import xaiLogoUrl from '@/assets/provider-logos/xai.svg'
import { cn } from '@/lib/cn'

const providerLogoUrls: Record<string, string> = {
  anthropic: anthropicLogoUrl,
  deepseek: deepseekLogoUrl,
  gemini: geminiLogoUrl,
  glm: glmLogoUrl,
  kimi: kimiLogoUrl,
  minimax: minimaxLogoUrl,
  openai: openaiLogoUrl,
  qwen: qwenLogoUrl,
  xai: xaiLogoUrl,
}

const providerLogoAliases: Record<string, string> = {
  alibaba: 'qwen',
  dashscope: 'qwen',
  google: 'gemini',
  moonshot: 'kimi',
  'moonshot-ai': 'kimi',
  'z.ai': 'glm',
  zhipu: 'glm',
  zhipuai: 'glm',
}

const fallbackColorClassName: Record<string, string> = {
  anthropic: 'bg-[#f3eee8] text-[#5f4637]',
  deepseek: 'bg-[#eef2ff] text-[#3959d8]',
  gemini: 'bg-[#eef7ff] text-[#1665c1]',
  glm: 'bg-[#111827] text-white',
  kimi: 'bg-[#fff4ea] text-[#a14600]',
  minimax: 'bg-[#fff8d8] text-[#8c6b00]',
  openai: 'bg-[#eef8f5] text-[#0f766e]',
  qwen: 'bg-[#eff6ff] text-[#1d4ed8]',
  xai: 'bg-[#f3f4f6] text-[#111827]',
}

type ProviderLogoVariant = 'default' | 'compact'

const logoVisualScaleClassName: Record<ProviderLogoVariant, Record<string, string>> = {
  compact: {
    deepseek: 'scale-[1.18]',
    glm: 'scale-[1.14]',
    minimax: 'scale-[1.24]',
    openai: 'scale-[1.14]',
  },
  default: {
    deepseek: 'scale-[1.24]',
    glm: 'scale-[1.2]',
    minimax: 'scale-[1.42]',
    openai: 'scale-[1.2]',
  },
}

function providerLabelFallback(provider: string) {
  const normalized = provider.trim()

  if (!normalized) {
    return '?'
  }

  if (normalized.length <= 4) {
    return normalized.toUpperCase()
  }

  return normalized.slice(0, 2).toUpperCase()
}

function normalizeProviderKey(provider: string) {
  const normalizedProvider = provider.trim().toLowerCase()

  return providerLogoAliases[normalizedProvider] ?? normalizedProvider
}

export function ProviderLogo({
  className,
  label,
  provider,
  variant = 'default',
}: {
  className?: string
  label?: string
  provider: string
  variant?: ProviderLogoVariant
}) {
  const normalizedProvider = normalizeProviderKey(provider)
  const logoUrl = providerLogoUrls[normalizedProvider]

  if (logoUrl) {
    return (
      <span
        aria-hidden
        className={cn(
          'inline-flex size-6 shrink-0 items-center justify-center rounded-md border border-[rgba(26,28,25,0.08)] bg-white p-1',
          className,
        )}
      >
        <img
          alt=""
          className={cn(
            'max-h-full max-w-full object-contain',
            logoVisualScaleClassName[variant][normalizedProvider],
          )}
          src={logoUrl}
        />
      </span>
    )
  }

  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex size-6 shrink-0 items-center justify-center rounded-md text-[10px] leading-none font-semibold',
        fallbackColorClassName[normalizedProvider] ??
          'bg-[var(--color-hover-muted)] text-[#374151]',
        className,
      )}
      title={label ?? provider}
    >
      {providerLabelFallback(label ?? provider)}
    </span>
  )
}
